<template>
    <div v-if="modelValue" class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-box">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Application Details</h5>
                <button class="btn btn-sm btn-light" @click="$emit('close')">&times;</button>
            </div>

            <div v-if="loading" class="text-center py-4">Loading...</div>

            <div v-else-if="data">
                <div class="row mb-3">
                    <div class="col-md-6">
                        <table class="table table-sm table-borderless mb-0">
                            <tr><th>Tracking No</th><td>{{ data.master.tracking_no }}</td></tr>
                            <tr><th>Name</th><td>{{ data.master.name }}</td></tr>
                            <tr><th>Mobile</th><td>{{ data.master.mobile }}</td></tr>
                            <tr><th>NID</th><td>{{ data.master.nid || '—' }}</td></tr>
                            <tr><th>Address</th><td>{{ data.master.address || '—' }}</td></tr>
                        </table>
                    </div>
                    <div class="col-md-6">
                        <table class="table table-sm table-borderless mb-0">
                            <tr><th>Application Type</th><td>{{ data.application_type_label }}</td></tr>
                            <tr><th>IP Address</th><td>{{ data.master.ip_address || '—' }}</td></tr>
                            <tr><th>Latitude</th><td>{{ data.master.latitude || '—' }}</td></tr>
                            <tr><th>Longitude</th><td>{{ data.master.longitude || '—' }}</td></tr>
                            <tr><th>Location</th><td>{{ data.master.location || '—' }}</td></tr>
                        </table>
                    </div>
                </div>

                <div v-if="data.generals && data.generals.length > 0" class="mb-3">
                    <h6 class="border-bottom pb-1">Additional Information</h6>
                    <table class="table table-sm table-bordered">
                        <tbody>
                            <tr v-for="g in data.generals" :key="g.key">
                                <th class="w-40">{{ g.label }}</th>
                                <td>{{ g.value }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="!rejectMode" class="d-flex gap-2 mt-3">
                    <button class="btn btn-success" @click="handleAccept">
                        <i class="fas fa-check"></i> Accept
                    </button>
                    <button class="btn btn-danger" @click="rejectMode = true">
                        <i class="fas fa-times"></i> Reject
                    </button>
                    <button class="btn btn-secondary ms-auto" @click="$emit('close')">Close</button>
                </div>

                <div v-if="rejectMode" class="mt-3">
                    <label class="form-label fw-bold">Rejection Reason <span class="text-danger">*</span></label>
                    <textarea
                        v-model="rejectComment"
                        class="form-control"
                        rows="3"
                        placeholder="Enter rejection reason..."
                    ></textarea>
                    <div class="d-flex gap-2 mt-2">
                        <button class="btn btn-danger" @click="handleReject" :disabled="!rejectComment.trim()">
                            Confirm
                        </button>
                        <button class="btn btn-secondary" @click="rejectMode = false">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import handleWaterSupply from '@/services/modules/waterSupply'

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    applicationId: { type: Number, default: null },
})

const emit = defineEmits(['close', 'accepted', 'rejected'])

const route = useRoute()
const { fetchPendingGeneralApplication, acceptGeneralApplication, rejectGeneralApplication } = handleWaterSupply()

const loading = ref(false)
const data = ref(null)
const rejectMode = ref(false)
const rejectComment = ref('')

watch(() => props.modelValue, (val) => {
    if (!val) {
        data.value = null
        rejectMode.value = false
        rejectComment.value = ''
        return
    }
    loadData()
})

const companyId = route.params.companyId

const loadData = async () => {
    loading.value = true
    data.value = null
    await fetchPendingGeneralApplication(props.applicationId + '?company_id=' + companyId).then(res => {
        if (res.status) data.value = res.data
    }).finally(() => {
        loading.value = false
    })
}

const handleAccept = async () => {
    if (!confirm('Accept this application? A project will be created.')) return
    await acceptGeneralApplication(props.applicationId, { company_id: companyId }).then(res => {
        emit(res.status ? 'accepted' : 'close', res.message)
    })
}

const handleReject = async () => {
    if (!rejectComment.value.trim()) return
    await rejectGeneralApplication(props.applicationId, { company_id: companyId, comments: rejectComment.value }).then(res => {
        emit(res.status ? 'rejected' : 'close', res.message)
    })
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}
.modal-box {
    background: #fff;
    border-radius: 8px;
    width: 100%;
    max-width: 780px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 1.5rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.w-40 {
    width: 40%;
}
</style>
